import { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { WifiOff, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

/**
 * One-click Network / system downtime toggle.
 * - First click → starts (auto-pauses any running task timer).
 * - Second click → ends. Shows live HH:MM:SS while active.
 * Available to all roles. Logged in the daily timesheet as [Network Downtime].
 */
export const NetworkDowntimeButton = () => {
  const [active, setActive] = useState(null);
  const [displaySec, setDisplaySec] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef(null);

  // Manual-entry dialog state (for after-the-fact logging when the user
  // couldn't tap the live toggle because the internet was actually down).
  const [manualOpen, setManualOpen] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manualNote, setManualNote] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/network-downtime/active')
      .then(res => { if (alive) setActive(res.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!active) {
      setDisplaySec(0);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    const compute = () => {
      try {
        const started = new Date(active.started_at).getTime();
        const sec = Math.max(0, Math.floor((Date.now() - started) / 1000));
        setDisplaySec(sec);
      } catch { /* noop */ }
    };
    compute();
    tickRef.current = setInterval(compute, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [active]);

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0
      ? `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
      : `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  };

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/network-downtime/toggle');
      if (res.data.state === 'started') {
        setActive({ id: res.data.id, started_at: res.data.started_at });
        toast.warning('Network downtime started — running task timer paused');
      } else {
        const mins = res.data.duration_minutes || 0;
        setActive(null);
        toast.success(`Network downtime ended (${mins} min logged in timesheet)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle network downtime');
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    const h = parseFloat(manualHours || 0);
    const m = parseFloat(manualMinutes || 0);
    const totalMinutes = Math.round(h * 60 + m);
    if (totalMinutes <= 0) {
      toast.error('Enter at least 1 minute of downtime');
      return;
    }
    if (totalMinutes > 24 * 60) {
      toast.error('A single entry cannot exceed 24 hours');
      return;
    }
    setManualSaving(true);
    try {
      await api.post('/network-downtime/manual', {
        minutes: totalMinutes,
        log_date: manualDate,
        note: manualNote || null,
      });
      toast.success(`Logged ${totalMinutes} min of network downtime for ${manualDate}`);
      setManualOpen(false);
      setManualHours(''); setManualMinutes(''); setManualNote('');
      setManualDate(new Date().toISOString().slice(0, 10));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log manual downtime');
    } finally {
      setManualSaving(false);
    }
  };

  // Live-toggle button (existing) — wrapped below with a manual-entry icon.
  const liveBtn = active ? (
      <Button
        size="default"
        onClick={toggle}
        disabled={busy}
        className="bg-red-600 hover:bg-red-700 text-white shadow-lg ring-2 ring-red-300 gap-1.5 rounded-l-full rounded-r-none px-4"
        data-testid="network-downtime-btn-active"
        title="End network downtime"
      >
        <WifiOff className="h-4 w-4" />
        <span>Network down</span>
        <span className="font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">{fmt(displaySec)}</span>
      </Button>
    ) : (
      <Button
        size="default"
        onClick={toggle}
        disabled={busy}
        className="bg-red-500 hover:bg-red-600 text-white shadow-lg hover:shadow-xl ring-2 ring-red-200 gap-1.5 rounded-l-full rounded-r-none px-4"
        data-testid="network-downtime-btn"
        title="Start network downtime"
      >
        <WifiOff className="h-4 w-4" />
        <span>Network</span>
      </Button>
    );

  return (
    <div className="inline-flex items-stretch">
      {liveBtn}
      <Button
        size="default"
        variant="outline"
        onClick={() => setManualOpen(true)}
        className="rounded-l-none rounded-r-full border-l-0 border-red-300 bg-white text-red-600 hover:bg-red-50 px-2.5"
        data-testid="network-downtime-manual-btn"
        title="Log network downtime manually (use when the internet was actually down)"
      >
        <Edit3 className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md" data-testid="network-downtime-manual-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <WifiOff className="h-5 w-5" /> Log network downtime
            </DialogTitle>
            <DialogDescription>
              Use this when the internet was actually down and you couldn&apos;t tap the live toggle. The duration is added to your daily timesheet.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Date</Label>
              <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} data-testid="network-manual-date" />
            </div>
            <div>
              <Label>Hours</Label>
              <Input type="number" min="0" step="1" value={manualHours} onChange={(e) => setManualHours(e.target.value)} placeholder="0" data-testid="network-manual-hours" />
            </div>
            <div>
              <Label>Minutes</Label>
              <Input type="number" min="0" step="1" max="59" value={manualMinutes} onChange={(e) => setManualMinutes(e.target.value)} placeholder="0" data-testid="network-manual-minutes" />
            </div>
            <div className="col-span-2">
              <Label>Reason (optional)</Label>
              <Textarea value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Power cut, BSNL outage, router restart…" className="min-h-[70px]" data-testid="network-manual-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} disabled={manualSaving}>Cancel</Button>
            <Button onClick={submitManual} disabled={manualSaving} className="bg-red-600 hover:bg-red-700" data-testid="network-manual-submit">
              {manualSaving ? 'Saving…' : 'Log downtime'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
